import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { FormattedMessage, useIntl } from "react-intl";
import { useParams, useSearchParams } from "react-router";
import { useAuth, useLinks } from "hooks";
import { INTERVIEW_TYPES_HUMANIZED } from "constants/index";
import { Grid, PageWrapper } from "components/Containers";
import {
  ProfileInterviewsAdvancedOptions,
  ProfileInterviewsDropdown,
  ProfileNav,
  ProfilePreferences,
  ProfileProgress,
  ProfileVideoInterviewsList,
} from "components/Profile";
import PageTitle from "components/PageTitle";
import { Banner } from "components/Banner";
import { SquadsHighlight } from "components/Squads";
import Card from "components/Card";
import { PrimaryLink } from "components/Links";
import { fontSize } from "style";
import { ApplicationDetailsModal } from "components/Missions/Applicants";
import { css } from "@emotion/react";

/**
 * ProfileVideoInterviews
 */
const ProfileVideoInterviews = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const intl = useIntl();
  const links = useLinks();
  const { isMember, isAdmin } = useAuth();
  const [interviews, setInterviews] = useState();
  const [selectedInterview, setSelectedInterview] = useState();
  const [application, setApplication] = useState();

  const mainColEnd = isMember ? 9 : 13;
  const missionRole = selectedInterview?.interview?.missionRole;
  const missionRoleApplicant = selectedInterview?.missionRoleApplicant;
  const selectedInterviewId = searchParams.get("selected");

  useEffect(() => {
    if (interviews) {
      setSelectedInterview(
        interviews.find((interview) => interview.uuid === selectedInterviewId) ||
          interviews.find((interview) => !interview.deletedAt)
      );
    }
  }, [interviews, selectedInterviewId]);

  return (
    <>
      <ProfileNav id={id} />
      <PageWrapper>
        <Helmet>
          <title>{intl.formatMessage({ id: "Meta.Profiles.VideoInterviews.Title" })}</title>
        </Helmet>
        <Grid topMargin={isMember}>
          {!isMember && (
            <Grid.col start={1} end={13}>
              <PageTitle
                subtitle={
                  missionRole ? (
                    <div css={styles.title}>
                      <FormattedMessage
                        id="Profile.VideoInterviews.MissionRole.Title"
                        values={{
                          role: (
                            <PrimaryLink to={links.mission_role_details(missionRole.mission.uuid, missionRole.slug)}>
                              {missionRole.name}
                            </PrimaryLink>
                          ),
                        }}
                      />
                      <PrimaryLink onClick={() => setApplication(missionRoleApplicant)} css={styles.application}>
                        <FormattedMessage id="Profile.VideoInterviews.MissionRole.ViewApplication" />
                      </PrimaryLink>
                    </div>
                  ) : (
                    <>{selectedInterview?.interview?.name}</>
                  )
                }
                actionBar={
                  <>
                    {isAdmin && (
                      <div>
                        {selectedInterview?.type && (
                          <Banner isClosable={false} css={{ lineHeight: 0.8 }}>
                            <FormattedMessage
                              id="Profile.VideoInterviews.InterviewType"
                              values={{
                                platform: INTERVIEW_TYPES_HUMANIZED[selectedInterview.type],
                              }}
                            />
                          </Banner>
                        )}
                      </div>
                    )}
                    <div>
                      <ProfileInterviewsDropdown
                        data={interviews}
                        onChange={(_name, val) => {
                          setSelectedInterview(interviews?.find((interview) => interview.id === val));
                        }}
                        value={selectedInterview?.id}
                        hasPermission={isAdmin}
                      />
                    </div>
                    <ProfileInterviewsAdvancedOptions
                      profileId={id}
                      interview={selectedInterview}
                      hasPermission={isAdmin}
                    />
                  </>
                }
              />
            </Grid.col>
          )}
          <Grid.col start={1} end={mainColEnd}>
            <ProfileVideoInterviewsList
              profileId={id}
              selectedInterview={selectedInterview}
              setInterviews={setInterviews}
            />
          </Grid.col>
          {isMember && (
            <Grid.col start={9} end={13}>
              <Grid>
                <Grid.col start={1} end={13}>
                  <Card>
                    <ProfileProgress id={id} />
                  </Card>
                </Grid.col>
                <Grid.col start={1} end={13}>
                  <Card>
                    <ProfilePreferences id={id} />
                  </Card>
                </Grid.col>
                <Grid.col start={1} end={13}>
                  <Card>
                    <SquadsHighlight profileId={id} />
                  </Card>
                </Grid.col>
              </Grid>
            </Grid.col>
          )}
        </Grid>
      </PageWrapper>
      <ApplicationDetailsModal application={application} show={!!application} setApplication={setApplication} />
    </>
  );
};

const styles = {
  title: css`
    display: flex;
    align-items: center;
    gap: 8px;
  `,
  application: css`
    white-space: nowrap;
    font-size: ${fontSize.normal};
  `,
};

export default ProfileVideoInterviews;
